<script setup lang="ts">
const props = defineProps({
  callback: { type: Function, required: true },
  error: { type: Function, required: true },
});

const { $getTranslate } = useNuxtApp();
const config = useRuntimeConfig();
const Logo = defineAsyncComponent(() => import("~/assets/svg/logo_google.svg"));

const GOOGLE_SDK_LOAD_TIMEOUT = 5000;

const googleButtonWrapper = ref<{ click: () => void } | null>(null);

// Cached per-instance promise for the SDK load, mirroring ButtonLoginFacebook's
// fbReadyPromise: a repeated click reuses the in-flight (or settled) load instead
// of inserting a second <script>.
let googleReadyPromise: Promise<void> | null = null;

// Component-scoped so onBeforeUnmount can clear a still-pending load timer
// (mirrors ButtonLoginFacebook). Otherwise a stale timeout fires fail() after the
// component is gone and removes the shared #google-jssdk <script>.
let sdkLoadTimeoutId: ReturnType<typeof setTimeout> | undefined;

// Disables the button for the whole load+login round-trip so a fast double-click
// can't stack two popups. The SDK is no longer loaded on mount (it set the
// `g_state` cookie before the user consented), so the click is what triggers the
// lazy load — mirroring ButtonLoginFacebook's isProcessing gate.
const isProcessing = ref(false);

const googleLoginCallback = (response: any) => {
  response ? props.callback(response) : props.error();
};

const ensureGoogleSDK = (): Promise<void> => {
  if (googleReadyPromise) return googleReadyPromise;

  googleReadyPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts) {
      resolve();
      return;
    }

    // Single bail-out path: clear the timer, drop the dead <script> so the next
    // click inserts a fresh one (with a fresh onerror), reset the cached promise
    // and reject so the caller surfaces the error and a retry is possible.
    const fail = (message: string) => {
      clearTimeout(sdkLoadTimeoutId);
      document.getElementById("google-jssdk")?.remove();
      googleReadyPromise = null;
      reject(new Error(message));
    };

    // Resolve once the GSI client script has loaded and exposed google.accounts.
    const onReady = () => {
      if (!window.google?.accounts) {
        fail("Google SDK loaded without google.accounts global");
        return;
      }
      clearTimeout(sdkLoadTimeoutId);
      resolve();
    };

    const existing = document.getElementById(
      "google-jssdk",
    ) as HTMLScriptElement | null;

    if (existing) {
      // Script already inserted (earlier mount/load) but not ready yet — wait for
      // its load/error events. (The window.google check above covers ready.)
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () =>
        fail("Google SDK failed to load"),
      );
    } else {
      const script = document.createElement("script");
      script.id = "google-jssdk";
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = onReady;
      script.onerror = () => fail("Google SDK failed to load");
      document.head.appendChild(script);
    }

    // Timeout backstop: a stalled or privacy-extension-neutered client.js that
    // never fires onload/onerror still surfaces as a rejection (mirrors FB).
    sdkLoadTimeoutId = setTimeout(
      () => fail("Google SDK load timed out"),
      GOOGLE_SDK_LOAD_TIMEOUT,
    );
  });

  return googleReadyPromise;
};

const createFakeGoogleWrapper = () => {
  const wrapper = document.createElement("div");
  wrapper.style.display = "none";
  wrapper.classList.add("custom-google-button");
  document.body.appendChild(wrapper);

  window.google!.accounts.id.renderButton(wrapper, {
    type: "icon",
    width: "200",
  });

  const button = wrapper.querySelector("div[role=button]") as HTMLElement;

  return { click: () => button?.click() };
};

const initGoogle = () => {
  if (!config.public.GOOGLE_CLIENT_ID || !window.google) return null;

  window.google.accounts.id.initialize({
    client_id: config.public.GOOGLE_CLIENT_ID,
    ux_mode: "popup",
    callback: googleLoginCallback,
  });

  googleButtonWrapper.value = createFakeGoogleWrapper();

  return googleButtonWrapper.value;
};

const logInWithGoogle = async () => {
  if (isProcessing.value) return;

  // No client id (env not configured) → do nothing, mirroring ButtonLoginFacebook.
  // Don't surface a user-facing error for what is a deployment config gap.
  if (!config.public.GOOGLE_CLIENT_ID) {
    console.warn("Google login unavailable: missing GOOGLE_CLIENT_ID");
    return;
  }

  isProcessing.value = true;

  try {
    if (googleButtonWrapper.value) {
      googleButtonWrapper.value.click();
      return;
    }

    await ensureGoogleSDK();

    const wrapper = initGoogle();
    if (wrapper) wrapper.click();
    else props.error();
  } catch (error) {
    console.error("Google SDK error:", error);
    props.error();
  } finally {
    // The popup is Google's own UI from here on; re-enable so a cancelled or
    // failed attempt never leaves the button permanently disabled.
    isProcessing.value = false;
  }
};

// Clear a still-pending SDK load timer if the component unmounts mid-load (e.g.
// the login modal is closed), so it can't fire fail() and remove the shared
// #google-jssdk <script> after teardown (mirrors ButtonLoginFacebook).
onBeforeUnmount(() => clearTimeout(sdkLoadTimeoutId));
</script>

<template>
  <button
    type="button"
    :disabled="isProcessing"
    @click.prevent="logInWithGoogle"
  >
    <component :is="Logo" class="w-6 h-6" />
    <span class="font-medium font-secondary 3xl:text-xl leading-tightest">
      {{ $getTranslate("login_form_button_google") }}
    </span>
  </button>
</template>

<style scoped lang="postcss">
button {
  @apply flex py-2.5 px-4 lg:py-3 3xl:py-4 items-center transition-colors duration-200 justify-center gap-4 w-full rounded bg-white hover:bg-[#eeeeee] active:bg-[#eeeeee] max-h-9 lg:max-h-10 3xl:max-h-11;
  box-shadow:
    0px 2px 3px 0px rgba(0, 0, 0, 0.17),
    0px 0px 3px 0px rgba(0, 0, 0, 0.08);
}
button span {
  color: rgba(0, 0, 0, 0.54);
}
</style>
